/*
 * Diverging-from-50% bars — for any complementary pair (left + right ≈ 100%).
 *
 * Bars grow out from a fixed center line: the left channel toward the left, the
 * right toward the right. Whoever's left channel reaches furthest is the most
 * "left-leaning" — the same layout serves one entity or several, so a single
 * player and an A-vs-B comparison read identically.
 *
 *  - StyleDiverging       offensive (cyan) vs defensive (navy) shot mix.
 *  - ConsistencyDiverging in-play (cyan) vs error (red) shot outcomes.
 *
 * Color is SEMANTIC per channel; entity identity comes from the per-row
 * avatar/dot (compare mode only), never the bar fill.
 */

import type React from 'react';
import { useTranslation } from 'react-i18next';
import { Scale, Sparkles } from 'lucide-react';
import { EntityAvatar } from './EntityAvatar';
import { SHOW_WINNERS } from '@/lib/constants';

/** One side of the split: its caption, footer hint, bar fill and text color. */
export interface DivergingChannel {
  label: string;
  foot: string;
  fill: string;
  ink: string;
}

export interface DivergingDatum {
  id: string;
  name: string;
  /** Entity identity color — used for the compare-row dot/avatar, not the bars. */
  color: string;
  crop?: string;
  /** Team rows: the members' crops, shown as a small avatar cluster. */
  crops?: string[];
  left: number; // 0..1
  right: number; // 0..1
}

/** Normalize the pair to fractions summing to 1 (guards a zero/partial total). */
function split(left: number, right: number): { l: number; r: number } {
  const total = left + right;
  if (total <= 0) return { l: 0, r: 0 };
  return { l: left / total, r: right / total };
}

/** Round to whole percents that always sum to 100 (0/0 → 0/0). */
function percents(left: number, right: number): { lPct: number; rPct: number } {
  const total = left + right;
  if (total <= 0) return { lPct: 0, rPct: 0 };
  const lPct = Math.round((left / total) * 100);
  return { lPct, rPct: 100 - lPct };
}

export const DivergingBars: React.FC<{
  data: DivergingDatum[];
  left: DivergingChannel;
  right: DivergingChannel;
  /** Hide the "more X / more Y" footer hints (e.g. when embedded in a card
      that doesn't need them). */
  hideFoot?: boolean;
  /** Keep the avatar/name column even with a single row — for comparisons where
      the surviving row is one of several entities and must stay identified. */
  forceIdentity?: boolean;
}> = ({ data, left, right, hideFoot = false, forceIdentity = false }) => {
  // One entity → drop the avatar/name column; the card title already names it.
  const single = data.length === 1 && !forceIdentity;

  return (
    <div className={`sop-div${single ? ' solo' : ''}`}>
      <div className="sop-div-axis">
        <span className="sop-div-axis-cap" style={{ color: left.ink }}>
          {left.label}
        </span>
        <span className="sop-div-axis-cap" style={{ color: right.ink }}>
          {right.label}
        </span>
      </div>

      <div className="sop-div-rows">
        {data.map((d) => {
          const { l, r } = split(d.left, d.right);
          const { lPct, rPct } = percents(d.left, d.right);
          return (
            <div className={`sop-div-row${single ? ' solo' : ''}`} key={d.id}>
              {!single ? (
                <div className="sop-div-id">
                  <EntityAvatar
                    name={d.name}
                    color={d.color}
                    crop={d.crop}
                    crops={d.crops}
                  />
                  <span className="cmp-name">{d.name}</span>
                </div>
              ) : null}
              <span className="sop-div-num off" style={{ color: left.ink }}>
                {lPct}%
              </span>
              <div className="sop-div-track">
                <span className="sop-div-center" aria-hidden="true" />
                <span
                  className="sop-div-fill off"
                  style={{ width: `${l * 50}%`, background: left.fill }}
                />
                <span
                  className="sop-div-fill def"
                  style={{ width: `${r * 50}%`, background: right.fill }}
                />
              </div>
              <span className="sop-div-num def" style={{ color: right.ink }}>
                {rPct}%
              </span>
            </div>
          );
        })}
      </div>

      {hideFoot ? null : (
        <div className="sop-div-foot">
          <span>{left.foot}</span>
          <span>{right.foot}</span>
        </div>
      )}
    </div>
  );
};

// ---- offensive vs defensive (style of play) -----------------------------

export interface StyleDatum {
  id: string;
  name: string;
  color: string;
  crop?: string;
  crops?: string[];
  offensive: number; // 0..1
  defensive: number; // 0..1
}

export const StyleDiverging: React.FC<{ data: StyleDatum[] }> = ({ data }) => {
  const { t } = useTranslation('tournament');
  const tl = (k: string) => t(`label.${k}` as never) as string;

  return (
    <DivergingBars
      data={data.map((d) => ({
        id: d.id,
        name: d.name,
        color: d.color,
        crop: d.crop,
        crops: d.crops,
        left: d.offensive,
        right: d.defensive,
      }))}
      left={{
        label: tl('offensive'),
        foot: tl('moreAttack'),
        fill: 'var(--accent)',
        ink: 'var(--accent-ink)',
      }}
      right={{
        label: tl('defensive'),
        foot: tl('moreDefense'),
        fill: 'var(--accent-deep)',
        ink: 'var(--accent-deep)',
      }}
    />
  );
};

// ---- in-play vs error (consistency) -------------------------------------

export interface ConsistencyDatum {
  id: string;
  name: string;
  color: string;
  crop?: string;
  crops?: string[];
  inRate: number; // 0..1
  errorRate: number; // 0..1
  winnerRate: number; // 0..1
}

export const ConsistencyDiverging: React.FC<{
  data: ConsistencyDatum[];
  hideFoot?: boolean;
  /** Leaves out the winners rows (Visão Geral charts winners itself). */
  hideWinners?: boolean;
}> = ({ data, hideFoot, hideWinners }) => {
  const { t } = useTranslation('tournament');
  const tl = (k: string) => t(`label.${k}` as never) as string;

  // Winners sit outside the in-play/error split — a winner is already counted
  // as in-play, so it can't be a third channel on that bar. They ride
  // underneath instead, one line per entity, with the winners-per-error ratio
  // that says whether the aggression paid for itself.
  //
  // Every entity gets a line, including a 0.0% one: a datum only exists when
  // the entity actually played shots, so zero winners is a real result worth
  // stating, not missing data to hide.
  const winners = SHOW_WINNERS && !hideWinners ? data : [];

  return (
    <>
      <DivergingBars
        hideFoot={hideFoot}
        data={data.map((d) => ({
          id: d.id,
          name: d.name,
          color: d.color,
          crop: d.crop,
          crops: d.crops,
          left: d.inRate,
          right: d.errorRate,
        }))}
        left={{
          label: tl('inRate'),
          foot: tl('moreConsistent'),
          fill: 'var(--accent)',
          ink: 'var(--accent-ink)',
        }}
        right={{
          label: tl('errorRate'),
          foot: tl('moreErrors'),
          fill: 'var(--red)',
          ink: 'var(--red)',
        }}
      />

      {winners.length ? (
        <div className="cons-winners">
          {winners.map((d) => {
            // A clean sheet has no ratio to show — dividing by zero errors is
            // undefined, and "∞ winners per error" reads as a glitch. It's also
            // the best thing that can happen on this bar, so the slot the ratio
            // would have taken celebrates it instead.
            const flawless = d.errorRate <= 0;
            return (
              <div key={d.id} className="cons-winner-row">
                {data.length > 1 ? (
                  <span
                    className="cons-winner-who"
                    style={{ color: d.color }}
                    title={d.name}
                  >
                    {d.name}
                  </span>
                ) : null}
                {/* Swatch for the winner rate, so it sits against the number
                    it labels — the identity leads the row, as in the bars. */}
                <span className="cons-winner-dot" aria-hidden="true" />
                <span className="cons-winner-stat">
                  <b>{(d.winnerRate * 100).toFixed(1)}%</b>
                  {t('awards.winnerRate')}
                </span>
                {flawless ? (
                  <span className="cons-winner-flawless">
                    <Sparkles size={12} strokeWidth={2.4} aria-hidden="true" />
                    {tl('flawless')}
                  </span>
                ) : (
                  // Both rates share a denominator, so it cancels — this is
                  // winners per error however the rates were scaled. Scales
                  // for the glyph: the number is what the aggression traded,
                  // reward weighed against cost.
                  <>
                    <span className="cons-winner-icon" aria-hidden="true">
                      <Scale size={12} strokeWidth={2} />
                    </span>
                    <span className="cons-winner-stat">
                      <b>{(d.winnerRate / d.errorRate).toFixed(1)}</b>
                      {tl('winnersPerError')}
                    </span>
                  </>
                )}
              </div>
            );
          })}
        </div>
      ) : null}
    </>
  );
};
